import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useState, useCallback } from 'react';
import { safeAuth, isExternalAuth } from './auth';
import { useIntl, defineMessages } from 'react-intl-next';
import { Inline } from '@atlaskit/primitives';
import WarningIcon from '@atlaskit/icon/core/migration/warning';
import { isOfTypeAuthError } from '@atlaskit/outbound-auth-flow-client/types';

// TODO: We should make appName non-optional later: http://go/j/AUX-570
// appName was made optional because it isn't in already inserted apps prior to the change in http://go/j/AUX-411
var defaultPromptMessage = function defaultPromptMessage(intl, appName) {
  return intl.formatMessage(messages.defaultPromptMessage, {
    appString: appName ? appName : intl.formatMessage(messages.thisAppLower)
  });
};
var externalAuthPromptMessage = function externalAuthPromptMessage(intl, appName) {
  return intl.formatMessage(messages.externalAuthPromptMessage, {
    appString: appName ? appName : intl.formatMessage(messages.thisAppUpper)
  });
};
export var useAuth = function useAuth(_ref) {
  var message = _ref.message,
    promptText = _ref.promptText,
    appName = _ref.appName,
    authUrl = _ref.authUrl,
    onSuccess = _ref.onSuccess,
    metadata = _ref.metadata;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    failedToAuthenticate = _useState2[0],
    setFailedToAuthenticate = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    authFailureMessage = _useState4[0],
    setAuthFailureMessage = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    loading = _useState6[0],
    setLoading = _useState6[1];
  var promptIsForExternalAuth = isExternalAuth(authUrl);
  var intl = useIntl();
  var startAuth = useCallback(function () {
    setLoading(true);
    setAuthFailureMessage(false);
    safeAuth(authUrl).then(function () {
      onSuccess().then(function (callbackAfterConsentFlowHasFinished) {
        setLoading(false);
        setFailedToAuthenticate(false);
        if (callbackAfterConsentFlowHasFinished && typeof callbackAfterConsentFlowHasFinished === 'function') {
          callbackAfterConsentFlowHasFinished();
        }
      });
    }, function (err) {
      setLoading(false);
      setFailedToAuthenticate(true);
      if (isOfTypeAuthError(err.type)) {
        setAuthFailureMessage(err.message);
      }
    });
  }, [authUrl, onSuccess, setLoading, setFailedToAuthenticate, setAuthFailureMessage]);
  var promptMessage = failedToAuthenticate ? authFailureMessage || intl.formatMessage(messages.defaultFailedToAuthMessage) : message || (promptIsForExternalAuth ? externalAuthPromptMessage(intl, appName) : defaultPromptMessage(intl, appName));
  var content = /*#__PURE__*/React.createElement(Inline, {
    alignBlock: "center"
  }, /*#__PURE__*/React.createElement(WarningIcon, {
    label: intl.formatMessage(messages.externalAuthWarning),
    color: "var(--ds-icon-warning, #FF8B00)",
    LEGACY_size: "medium",
    spacing: "spacious"
  }), promptMessage);
  var externalAuthButtonText = intl.formatMessage(messages.configureAccess);
  var buttonText = promptText || (promptIsForExternalAuth ? externalAuthButtonText : intl.formatMessage(messages.allowAccess));
  return {
    loading: loading,
    startAuth: startAuth,
    promptMessage: content,
    buttonText: buttonText
  };
};
var messages = defineMessages({
  defaultPromptMessage: {
    id: 'confluence.threeLOPrompt.use-auth.default.prompt.message',
    defaultMessage: 'For {appString} to display, you need to allow the app to access Atlassian products on your behalf.',
    description: 'A text prompt that tells a user that an app needs to be given permission before the app can be used.'
  },
  externalAuthPromptMessage: {
    id: 'confluence.threeLOPrompt.use-auth.external.auth.prompt.message',
    defaultMessage: '{appString} requires additional access to your account.',
    description: 'A text prompt indicating that an app requires additional permissions before the app can be used.'
  },
  thisAppUpper: {
    id: 'confluence.threeLOPrompt.use-auth.this.app.upper.message',
    defaultMessage: 'This app',
    description: 'a default message shown to the user when the name of an application is unknown.'
  },
  thisAppLower: {
    id: 'confluence.threeLOPrompt.use-auth.this.app.lower.message',
    defaultMessage: 'this app',
    description: 'a default message shown to the user when the name of an application is unknown.'
  },
  defaultFailedToAuthMessage: {
    id: 'confluence.threeLOPrompt.use-auth.default.failed.to.auth.message',
    defaultMessage: 'Failed to authenticate.',
    description: 'A default text prompt to indicate than an app was unable to authenticate a user.'
  },
  externalAuthWarning: {
    id: 'confluence.threeLOPrompt.use-auth.external.auth.warning.message',
    defaultMessage: 'External Auth Warning',
    description: 'A default text prompt to indicate than an app was unable to authenticate a user.'
  },
  configureAccess: {
    id: 'confluence.threeLOPrompt.use-auth.configure.access.button.text',
    defaultMessage: 'Configure access',
    description: "Label for a button that allows a user to configure the app's access to the user's account"
  },
  allowAccess: {
    id: 'confluence.threeLOPrompt.use-auth.allow.access.button.text',
    defaultMessage: 'Allow access',
    description: 'Label for a button that would allow the user to grant access to some application.'
  }
});